/**
 * Persistencia local del player.
 * Usa localStorage para el deviceId y el authToken; IndexedDB (vía Cache API
 * del Service Worker) para los medios cacheados.
 */

import type { PlayerConfig } from './api';

const DEVICE_ID_KEY = 'signage:device-id';
const AUTH_TOKEN_KEY = 'signage:auth-token';
const CONTENT_VERSION_KEY = 'signage:content-version';
const CONFIG_KEY = 'signage:last-config';

/**
 * Identificador único de este dispositivo. Se genera la primera vez y persiste.
 * Si el localStorage se borra, la pantalla queda como si fuera nueva → ciclo de pairing.
 */
export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

function generateDeviceId(): string {
  // 24 chars alfanuméricos
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 24; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
}

export function loadAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function saveAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function loadContentVersion(): string | null {
  return localStorage.getItem(CONTENT_VERSION_KEY);
}

export function saveContentVersion(version: string): void {
  localStorage.setItem(CONTENT_VERSION_KEY, version);
}

/**
 * Guarda la última config OK. Así, si el player reinicia mientras el server o
 * la red están caídos, puede seguir reproduciendo lo último que supo en vez de
 * quedar en negro. Los BYTES de los medios ya los cachea el Service Worker;
 * acá guardamos la ESTRUCTURA (qué mostrar, en qué orden, cuánto).
 */
export function saveConfig(config: PlayerConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  } catch {
    /* localStorage lleno/no disponible: ignorar, no es crítico */
  }
}

/** Última config persistida, o null si nunca se guardó. */
export function loadConfig(): PlayerConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    return raw ? (JSON.parse(raw) as PlayerConfig) : null;
  } catch {
    return null;
  }
}
